import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { usePlus } from '../context/PlusContext';
import { X } from 'lucide-react';
import { IconPicker, GRUPOS_ICONOS } from './IconPicker';
import { aNumero, AVISO_IMPORTE, PATRON_IMPORTE } from '../utils/numeros';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';

const GRUPOS_CUENTAS = GRUPOS_ICONOS.filter(g =>
  ['Finanzas', 'Hogar', 'Tecnología', 'Viajes', 'Otros'].includes(g.label)
);

interface ModalAgregarCuentaProps {
  onClose: () => void;
}

export const ModalAgregarCuenta: React.FC<ModalAgregarCuentaProps> = ({ onClose }) => {
  useCerrarConEscape(onClose);
  const { crearCuenta } = useData();
  const { ofrecerSiEsLimite } = usePlus();
  const [nombre, setNombre]           = useState('');
  const [tipo, setTipo]               = useState<'corriente' | 'ahorro' | 'tarjeta' | 'hucha'>('corriente');
  const [icono, setIcono]             = useState('🏦');
  const [saldo, setSaldo]             = useState('');
  const [objetivo, setObjetivo]       = useState('');
  const [ocultarSaldo, setOcultarSaldo] = useState(false);
  const [esCredito, setEsCredito]     = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  const inputClass = "w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white";
  const labelClass = "block text-gray-700 dark:text-gray-300 font-semibold mb-2";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!nombre) { setError('Ponle un nombre a la cuenta'); return; }
    setLoading(true);
    try {
      await crearCuenta({
        nombre,
        tipo,
        icono,
        // En blanco es lo mismo que 0: no obligamos a escribirlo
        saldo: saldo.trim() === '' ? 0 : aNumero(saldo),
        ocultarSaldo,
        esCredito,
        ...(tipo === 'hucha' && objetivo ? { objetivo: aNumero(objetivo) } : {}),
      });
      onClose();
    } catch (err: any) {
      // Sin hueco en el plan gratis: en vez del error, la hoja de Plus
      if (ofrecerSiEsLimite(err)) { onClose(); return; }
      setError(err.message || 'Error al crear cuenta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center capa-modal z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-full overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Nueva Cuenta</h2>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="boton-cerrar text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}

          <div>
            <label className={labelClass}>Nombre</label>
            <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
              className={inputClass} placeholder="Ej: Santander Corriente" required />
          </div>

          <div>
            <label className={labelClass}>Tipo</label>
            <select value={tipo} onChange={e => setTipo(e.target.value as typeof tipo)}
              className={inputClass}>
              <option value="corriente">Corriente</option>
              <option value="ahorro">Ahorro</option>
              <option value="tarjeta">Tarjeta de Crédito</option>
              <option value="hucha">🐷 Hucha</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>Icono</label>
            <IconPicker value={icono} onChange={setIcono} grupos={GRUPOS_CUENTAS} />
          </div>

          <div>
            <label className={labelClass}>Saldo Inicial (€) — opcional</label>
            <input type="text" inputMode="decimal" pattern="-?[0-9]*[.,]?[0-9]*" title={AVISO_IMPORTE}
              value={saldo} onChange={e => setSaldo(e.target.value)}
              className={inputClass} placeholder="0,00" />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Si lo dejas en blanco, empieza en 0. Se puede cambiar después.</p>
          </div>

          {tipo === 'hucha' && (
            <div>
              <label className={labelClass}>Importe objetivo (€) — opcional</label>
              <input type="text" inputMode="decimal" pattern={PATRON_IMPORTE} title={AVISO_IMPORTE} value={objetivo} onChange={e => setObjetivo(e.target.value)}
                className={inputClass} placeholder="Ej: 90.00" />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Importe total que quieres acumular</p>
            </div>
          )}

          <div className="flex items-center gap-3">
            <input type="checkbox" id="ocultarSaldo" checked={ocultarSaldo}
              onChange={e => setOcultarSaldo(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded" />
            <label htmlFor="ocultarSaldo" className="text-gray-700 dark:text-gray-300">
              Ocultar saldo
            </label>
          </div>

          <div className="flex items-center gap-3">
            <input type="checkbox" id="esCredito" checked={esCredito}
              onChange={e => setEsCredito(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded" />
            <label htmlFor="esCredito" className="text-gray-700 dark:text-gray-300">
              Tarjeta de crédito
            </label>
          </div>

          <div className="flex gap-4 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:opacity-50">
              {loading ? 'Creando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
